import React, { JSX, useCallback, useEffect, useRef, useState } from 'react';
import MuiIconButton from '@mui/material/IconButton';
import { Theme } from '@mui/material/styles';
import { withStyles } from 'tss-react/mui';
import MuiDrawer, { DrawerProps } from '@mui/material/Drawer';
import classNames from 'classnames';
import EDTypography from '../../Typography/EDTypography';
import EDIconButton from '../../Buttons/IconButton/EDIconButton';
import { MenuContext, IToggleOpen } from '../../../Features/MenuContext';
import EDIcon from '../../Icon/EDIcon';
import { IconType } from '../../Icon/Utils/IconDefinitions';
import {
  APP_NAVBAR_HEIGHT_PX,
  AvenyThemeSpacing,
} from '../../ThemeProvider/AvenyTheme';
import { useIsExtraSmallScreen } from '../../../Hooks/useFullscreenView';

export interface IHeader {
  title?: string;
  icon?: IconType | JSX.Element;
  disabled?: boolean;
  disabledReason?: string;
  iconRow?: React.ReactNode;
  onToggle: () => void;
  content?: React.ReactNode;
  closeIcon?: boolean;
  rightOfIconArea?: React.ReactNode;
}

interface IProps {
  type?: 'permanent' | 'persistent' | 'pseudopersistent' | 'temporary';
  anchor?: 'left' | 'top' | 'right' | 'bottom';
  children: React.ReactNode;
  open: boolean;
  header?: IHeader;
  pinnable?: boolean;
  toggleClosed?: () => void;
  onPinned?: (pinned: boolean) => void;
  /*
    A string as a DOM element or a component
  */
  component?:
    | string
    | React.ComponentType<
        React.PropsWithChildren<{ children: React.ReactNode }>
      >;
  noHeaderPadding?: boolean;
  testid?: string;
  classes?: Partial<Record<keyof ReturnType<typeof styles>, string>>;
  className?: string;
  style?: React.CSSProperties;
  firstItemsSkipMarginBottom?: boolean;
}

interface IInnerProps extends IProps {
  menuContextToggleOpen: IToggleOpen;
}

const drawerWidth = 320;
const styles = (theme: Theme, _: any, classes: any) => ({
  header: {
    width: '100%',
    justifyContent: 'space-between' as 'space-between',
    padding: theme.spacing(0, 3, 0, 1.5),
    boxSizing: 'border-box' as 'border-box',
  },
  headerNoPadding: {
    padding: theme.spacing(0),
  },
  firstItems: {
    backgroundColor: theme.palette.secondary.main,
    color: theme.palette.common.white,
    '& a': {
      '&:visited': {
        color: theme.palette.common.white,
      },
      '&:link': {
        color: theme.palette.common.white,
      },
    },
  },
  firstItemsSkipMarginBottom: {
    [`&.${classes.firstItems}`]: {
      marginBottom: 0,
    },
  },
  firstItemsIcon: {
    paddingTop: theme.spacing(2),
  },
  noFirstItems: {
    padding: theme.spacing(1, 0, 0),
    backgroundColor: theme.palette.secondary.main,
    color: theme.palette.common.white,
  },
  buttonRoot: {
    color: theme.palette.common.white,
    padding: theme.spacing(0.5),
    marginLeft: theme.spacing(2),
    marginRight: theme.spacing(1.5),
  },
  drawerDocked: {
    position: 'fixed' as 'fixed',
    left: '0',
    top: '0',
    maxWidth: '0',
  },
  drawerNotPinned: {
    zIndex: 1200,
  },
  drawerPinned: {
    zIndex: 1,
  },
  drawerPaper: {
    height: '100%',
    width: drawerWidth,
  },
  drawerPaperShadow: {
    boxShadow: theme.shadows[16],
  },
  paperAnchorDockedLeft: {
    borderRight: 'none' as 'none',
  },
  paperAnchorDockedRight: {
    borderLeft: 'none',
  },
  clippedPersistent: {
    top: theme.spacing(8),
  },
  drawerIcon: {
    position: 'absolute',
    right: `${AvenyThemeSpacing + 1}px`,
    top: theme.spacing(0.75),
    padding: theme.spacing(1),
  },
  pinIcon: {
    opacity: 0,
    transition: 'opacity 100ms ease',
    [`.${classes.drawerPaper}:hover &`]: {
      opacity: 1,
    },
  },
  flexContainer: {
    display: 'flex',
    alignItems: 'center',
    minHeight: APP_NAVBAR_HEIGHT_PX,
  },
});

const slideProps = {
  unmountOnExit: true,
};

/**
 * Standardmeny som fälls ut från endera sidan.
 */
export const EDDrawer = (props: IInnerProps) => {
  const {
    anchor,
    menuContextToggleOpen: menuContextToggleOpenProp,
    onPinned,
    pinnable,
    header,
  } = props;
  const [pinned, setPinned] = useState(false);
  const previousOpenRef = useRef(props.open);
  const classes = withStyles.getClasses(props);
  const isExtraSmall = useIsExtraSmallScreen();

  const menuContextToggleOpen = useCallback(
    (value: boolean) => {
      if (anchor === 'left' || anchor === 'right') {
        menuContextToggleOpenProp(anchor, value);
      }
    },
    [anchor, menuContextToggleOpenProp]
  );

  useEffect(() => {
    if (pinnable && props.open !== previousOpenRef.current) {
      menuContextToggleOpen(props.open && pinned);
    }
    previousOpenRef.current = props.open;
  }, [pinnable, props.open, menuContextToggleOpen, pinned]);

  const onPin = useCallback(() => {
    setPinned(previousPinned => {
      const nextPinned = !previousPinned;
      menuContextToggleOpen(nextPinned);
      if (onPinned) {
        onPinned(nextPinned);
      }
      return nextPinned;
    });
  }, [menuContextToggleOpen, onPinned]);

  const onToggle = () => {
    header?.onToggle();
  };

  const handleClose = (_event: object, _reason: string) => {
    if ((props.type ?? 'temporary') === 'temporary' && props.toggleClosed) {
      props.toggleClosed();
    }
  };

  const renderHeaderIcon = () => {
    if (!header) {
      return null;
    }

    if (pinned) {
      if (typeof header.icon === 'string') {
        return <EDIcon color="inherit" iconType={header.icon} />;
      }
      return header.icon;
    }

    if (header.icon) {
      return (
        <div className={classes.flexContainer}>
          <EDIconButton
            tooltip=""
            icon={
              typeof header.icon === 'string' ? (
                <EDIcon color="inherit" iconType={header.icon} />
              ) : (
                header.icon
              )
            }
            onClick={onToggle}
            disabled={header.disabled}
            disabledReason={header.disabledReason}
            padding={0.5}
            margin={'0px 8px'}
            colorWhite
          />
          {header.rightOfIconArea}
        </div>
      );
    } else {
      return header.rightOfIconArea || null;
    }
  };

  const type = props.type ? props.type : 'temporary';
  const ComponentProp = props.component ? props.component : 'div';

  const muiDrawerProps: DrawerProps = {
    variant: type === 'pseudopersistent' || pinned ? 'persistent' : type,
    elevation:
      type === 'pseudopersistent' || type === 'temporary' || pinned ? 16 : 0,
    classes: {
      paper: classNames(classes.drawerPaper, {
        [classes.drawerPaperShadow]: !pinned && pinnable,
        [classes.clippedPersistent]: pinned,
      }),
      docked: classNames(classes.drawerDocked, {
        [classes.drawerNotPinned]: !pinned && pinnable,
        [classes.drawerPinned]: pinned,
      }),
      paperAnchorDockedLeft: classes.paperAnchorDockedLeft,
      paperAnchorDockedRight:
        !pinned && pinnable ? classes.paperAnchorDockedRight : undefined,
    },
    open: props.open,
    anchor: props.anchor,
    ModalProps: {
      keepMounted: true,
      onClose: handleClose,
    },
    style: props.style,
    className: props.className,
  };

  return (
    <MuiDrawer
      {...muiDrawerProps}
      SlideProps={props.anchor === 'right' ? slideProps : undefined}
      data-testid={props.testid}
      componentsProps={{
        backdrop: {
          'data-testid': `${props.testid ?? 'drawer'}-backdrop`,
        } as any,
      }}
    >
      <ComponentProp>
        {props.header && (
          <div
            className={classNames({
              [classes.firstItems]: !pinnable,
              [classes.firstItemsSkipMarginBottom]:
                props.firstItemsSkipMarginBottom,
              [classes.noFirstItems]: pinnable,
              [classes.firstItemsIcon]: typeof props.header.icon === 'string',
            })}
          >
            <div
              className={classNames(classes.header, {
                [classes.headerNoPadding]: props.noHeaderPadding,
              })}
            >
              {!pinnable && renderHeaderIcon()}
              {props.header.title && (
                <EDTypography variant="h6" color="inherit">
                  {props.header.title}
                </EDTypography>
              )}
              {props.header.closeIcon && !pinned && (
                <MuiIconButton
                  onClick={onToggle}
                  classes={{
                    root: classes.buttonRoot,
                  }}
                >
                  <EDIcon color="inherit" iconType="close" />
                </MuiIconButton>
              )}
              {props.header.content}
              {pinnable && (
                <>
                  {isExtraSmall && (
                    <>
                      <EDIconButton
                        tooltip=""
                        margin="dense"
                        padding="none"
                        className={classes.drawerIcon}
                        color="inherit"
                        onClick={props.toggleClosed}
                        icon={
                          <EDIcon
                            color="inherit"
                            size="small"
                            iconType={'close'}
                          />
                        }
                      />
                    </>
                  )}
                  {!isExtraSmall && (
                    <>
                      <EDIconButton
                        tooltip={pinned ? 'Lossa menyn' : 'Fäst menyn'}
                        margin="dense"
                        padding="none"
                        className={classNames(
                          classes.drawerIcon,
                          classes.pinIcon
                        )}
                        color="inherit"
                        onClick={onPin}
                        icon={
                          <EDIcon
                            color="inherit"
                            size="small"
                            iconType={pinned ? 'pinOff' : 'pin'}
                          />
                        }
                      />
                    </>
                  )}
                </>
              )}
            </div>
          </div>
        )}
        {props.children}
      </ComponentProp>
    </MuiDrawer>
  );
};

const EDEnhancedDrawer = (props: IProps) => {
  return (
    <MenuContext.Consumer>
      {({ toggleOpen }) => (
        <EDDrawer menuContextToggleOpen={toggleOpen} {...props} />
      )}
    </MenuContext.Consumer>
  );
};

export default withStyles(EDEnhancedDrawer, styles, {
  uniqId: '5d4c0bf7-7e2d-4a97-94db-8f38c131163e',
});
